import logger from 'ws-scripts/modules/logger';
import { publishEvent } from '../utilities/pubsub';
import {
	isValidPostalCode,
	parseRange,
	parseExcludeByRange
} from '../utilities/locationUtils';

// Configuration Constants
const DEFAULT_RANGE = 50;
const DEFAULT_EXCLUDE_BY_RANGE = true;
const WIDGET_NAME = 'ws-locations-data';
const TOPIC_LOCATIONS = 'locations';
const TOPIC_LOCATION_SELECTED = 'location-selected';

const LOG_PREFIX = `[${WIDGET_NAME}][LocationsData]`;

export class LocationsData {
	constructor(requestLocations) {
		this.requestLocations = requestLocations;
		this.locations = [];
		this.postalCode = '';
		this.selectedLocationCoordinates = null;
		this.range = DEFAULT_RANGE;
		this.excludeByRange = DEFAULT_EXCLUDE_BY_RANGE;
		this.error = null;
		this.TOPIC_PREFIX = WIDGET_NAME;
		this.LOCATIONS_TOPIC = `${this.TOPIC_PREFIX}/${TOPIC_LOCATIONS}`;
		this.LOCATION_SELECTED_TOPIC = `${this.TOPIC_PREFIX}/${TOPIC_LOCATION_SELECTED}`;
		logger.debug(`${LOG_PREFIX} Constructor called`);
	}

	// Register callback for location updates
	registerLocationsNotification(callback) {
		logger.debug(`${LOG_PREFIX} Registering subscriber`, {
			topic: this.LOCATIONS_TOPIC
		});

		this.subscribeEvent(this.LOCATIONS_TOPIC, callback);

		// Return unsubscribe function
		return () => {
			logger.debug(`${LOG_PREFIX} Unsubscribing`, {
				topic: this.LOCATIONS_TOPIC
			});
			this.unsubscribeEvent(this.LOCATIONS_TOPIC, callback);
		};
	}


	// Register callback for location selection events
	registerLocationSelectedNotification(callback) {
		logger.info(`${LOG_PREFIX} Registering location selected subscriber`, {
			topic: this.LOCATION_SELECTED_TOPIC
		});

		this.subscribeEvent(this.LOCATION_SELECTED_TOPIC, callback);

		// Return unsubscribe function
		return () => {
			logger.info(`${LOG_PREFIX} Unsubscribing from location selected`, {
				topic: this.LOCATION_SELECTED_TOPIC
			});
			this.unsubscribeEvent(this.LOCATION_SELECTED_TOPIC, callback);
		};
	}

	// Get all locations
	getLocations() {
		return this.locations;
	}

	// Get current selected location coordinates
	getSelectedLocationCoordinates() {
		return this.selectedLocationCoordinates;
	}

	// Get error message if any
	getError() {
		return this.error;
	}

	setSearchParams(postalCode, range, excludeByRange) {
		if (!isValidPostalCode(postalCode)) {
			logger.error(`${LOG_PREFIX} Invalid postal code:`, postalCode);
			this.locations = [];
			this.error = 'Invalid postal code';
			publishEvent(this.LOCATIONS_TOPIC);
			return;
		}

		const trimmedPostalCode = postalCode.trim();
		const newRange = parseRange(range, this.range);
		const newExcludeByRange = parseExcludeByRange(
			excludeByRange,
			this.excludeByRange
		);

		const hasChanged =
			trimmedPostalCode !== this.postalCode ||
			newRange !== this.range ||
			newExcludeByRange !== this.excludeByRange;

		if (!hasChanged) {
			logger.debug(`${LOG_PREFIX} No changes detected, skipping fetch`);
			return;
		}

		this.postalCode = trimmedPostalCode;
		this.range = newRange;
		this.excludeByRange = newExcludeByRange;

		logger.debug(`${LOG_PREFIX} Parameters updated:`, {
			postalCode: this.postalCode,
			range: this.range,
			excludeByRange: this.excludeByRange
		});

		this._fetchLocations();
	}

	// Set location with coordinates
	setLocationWithCoordinates({ coordinates }) {
		logger.info(`${LOG_PREFIX} Setting location with coordinates`, {
			coordinates
		});

		// Store selected location coordinates
		this.selectedLocationCoordinates = coordinates;

		// Publish location selected event
		publishEvent(this.LOCATION_SELECTED_TOPIC, {
			coordinates
		});
	}

	// Trigger location fetch
	_fetchLocations() {
		logger.debug(`${LOG_PREFIX} Triggering fetch`, {
			postalCode: this.postalCode,
			range: this.range,
			excludeByRange: this.excludeByRange
		});

		this.requestLocations({
			postalCode: this.postalCode,
			range: this.range,
			excludeByRange: this.excludeByRange
		});
	}

	// eslint-disable-next-line class-methods-use-this
	subscribeEvent(topic, callback) {
		if (window?.DDC?.pubsub) {
			logger.debug(`${LOG_PREFIX} Subscribing`, { topic });
			window.DDC.pubsub.subscribe(topic, callback);

			const subscribers = window.DDC.pubsub.subscribers?.[topic];
			logger.debug(`${LOG_PREFIX} Subscribed`, {
				topic,
				totalSubscribers: subscribers?.length || 0
			});
		} else {
			logger.warn(`${LOG_PREFIX} pubsub not available`, { topic });
		}
	}

	// Unsubscribe from event through PubSub
	// eslint-disable-next-line class-methods-use-this
	unsubscribeEvent(topic, callback) {
		if (window?.DDC?.pubsub) {
			logger.debug(`${LOG_PREFIX} Unsubscribing`, { topic });
			window.DDC.pubsub.unsubscribe(topic, callback);

			const subscribers = window.DDC.pubsub.subscribers?.[topic];
			logger.debug(`${LOG_PREFIX} Unsubscribed`, {
				topic,
				totalSubscribers: subscribers?.length || 0
			});
		} else {
			logger.warn(`${LOG_PREFIX} pubsub not available for unsubscribe`, {
				topic
			});
		}
	}
}
